import { useState, useEffect } from 'react';
import { ClientSelector } from './ClientSelector';
import { PMSelector } from './PMSelector';

interface JobFormProps {
  onSubmit: (data: Record<string, unknown>) => void;
  onCancel?: () => void;
  initialData?: Record<string, unknown>;
}

export const JobForm = ({ onSubmit, onCancel, initialData }: JobFormProps) => {
  const [formData, setFormData] = useState({
    title: '',
    client_id: '',
    site_address: '',
    start_date: '',
    estimated_end_date: '',
    project_manager_id: '',
    pm_source: 'user',
    project_manager_custom: '',
    budget: '',
    description: '',
    status: 'scheduled',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        title: initialData.title as string || '',
        client_id: initialData.clientId as string || '',
        site_address: initialData.siteAddress as string || '',
        start_date: ((initialData.startDate as string) || '').split('T')[0],
        estimated_end_date: ((initialData.estimatedEndDate as string) || '').split('T')[0],
        project_manager_id: initialData.projectManagerId as string || '',
        pm_source: (initialData.pmSource as string) || 'user',
        project_manager_custom: (initialData.projectManagerCustom as string) || '',
        budget: initialData.budget as string || '',
        description: initialData.description as string || '',
        status: initialData.status as string || 'scheduled',
      });
    }
  }, [initialData]);

  const isWeekend = (dateString: string): boolean => {
    if (!dateString) return false;
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const dayOfWeek = date.getDay();
    return dayOfWeek === 0 || dayOfWeek === 6;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Job title is required';
    }

    if (!formData.client_id) {
      newErrors.client_id = 'Please select a client';
    }

    if (!formData.start_date) {
      newErrors.start_date = 'Start date is required';
    } else if (isWeekend(formData.start_date)) {
      newErrors.start_date = 'Start date cannot be on a weekend (Saturday or Sunday)';
    }

    if (!formData.estimated_end_date) {
      newErrors.estimated_end_date = 'Estimated end date is required';
    } else if (isWeekend(formData.estimated_end_date)) {
      newErrors.estimated_end_date = 'End date cannot be on a weekend (Saturday or Sunday)';
    }

    if (formData.start_date && formData.estimated_end_date && formData.start_date > formData.estimated_end_date) {
      newErrors.estimated_end_date = 'End date must be after start date';
    }

    if (formData.budget && parseFloat(formData.budget) <= 0) {
      newErrors.budget = 'Budget must be a positive number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validate()) {
      onSubmit(formData);
    }
  };

  const inputClasses = "form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-lg text-gray-900 focus:outline-0 focus:ring-2 focus:ring-primary border-gray-200 bg-gray-50 h-10 px-4 text-sm font-normal leading-normal";
  const labelClasses = "block text-sm font-medium text-gray-900 mb-2";
  const errorClasses = "text-error text-xs mt-1";

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="space-y-6">
        {/* Job Title */}
        <div>
          <label htmlFor="title" className={labelClasses}>
            Job Title <span className="text-error">*</span>
          </label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title || ''}
            onChange={handleChange}
            className={inputClasses}
            placeholder="e.g., Kitchen Remodel - Johnson Residence"
          />
          {errors.title && <p className={errorClasses}>{errors.title}</p>}
        </div>

        {/* Client Selector */}
        <ClientSelector
          value={formData.client_id ? parseInt(formData.client_id) : null}
          onChange={(clientId) => {
            setFormData(prev => ({ ...prev, client_id: clientId.toString() }));
            if (errors.client_id) {
              setErrors(prev => ({ ...prev, client_id: '' }));
            }
          }}
          error={errors.client_id}
        />

        {/* Site Address */}
        <div>
          <label htmlFor="siteAddress" className={labelClasses}>
            Site Address
          </label>
          <input
            type="text"
            id="site_address"
            name="site_address"
            value={formData.site_address || ''}
            onChange={handleChange}
            className={inputClasses}
            placeholder="123 Main St, City, State ZIP"
          />
        </div>

        {/* Dates */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="startDate" className={labelClasses}>
              Start Date <span className="text-error">*</span>
            </label>
            <input
              type="date"
              id="start_date"
              name="start_date"
              value={formData.start_date || ''}
              onChange={handleChange}
              className={inputClasses}
            />
            {formData.start_date && isWeekend(formData.start_date) && (
              <p className="text-xs text-error mt-1 flex items-center gap-1">
                <span className="material-symbols-outlined text-sm">warning</span>
                Weekends are not allowed for job start dates
              </p>
            )}
            {errors.start_date && <p className={errorClasses}>{errors.start_date}</p>}
          </div>

          <div>
            <label htmlFor="estimatedEndDate" className={labelClasses}>
              Estimated End Date <span className="text-error">*</span>
            </label>
            <input
              type="date"
              id="estimated_end_date"
              name="estimated_end_date"
              value={formData.estimated_end_date || ''}
              onChange={handleChange}
              className={inputClasses}
            />
            {formData.estimated_end_date && isWeekend(formData.estimated_end_date) && (
              <p className="text-xs text-error mt-1 flex items-center gap-1">
                <span className="material-symbols-outlined text-sm">warning</span>
                Weekends are not allowed for job end dates
              </p>
            )}
            {errors.estimated_end_date && <p className={errorClasses}>{errors.estimated_end_date}</p>}
          </div>
        </div>

        {/* Project Manager — a user, the client, or a custom name (CR #97) */}
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            {([
              ['user', 'Team member'],
              ['client', 'The client'],
              ['custom', 'Custom'],
            ] as const).map(([src, label]) => (
              <button
                key={src}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, pm_source: src }))}
                className={`h-7 px-2.5 rounded-full text-xs font-semibold border transition-colors ${
                  formData.pm_source === src
                    ? 'bg-primary text-white border-primary'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-primary/50'
                }`}
              >
                {label}
              </button>
            ))}
            <span className="text-xs text-gray-400 ml-1">who runs this project?</span>
          </div>
          {formData.pm_source === 'user' && (
            <PMSelector
              value={formData.project_manager_id ? parseInt(formData.project_manager_id) : null}
              onChange={(pmId) => {
                setFormData(prev => ({ ...prev, project_manager_id: pmId ? pmId.toString() : '' }));
                if (errors.project_manager_id) {
                  setErrors(prev => ({ ...prev, project_manager_id: '' }));
                }
              }}
              error={errors.project_manager_id}
            />
          )}
          {formData.pm_source === 'client' && (
            <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5">
              The job's client acts as project manager — their name and contact details show on the PM card.
            </p>
          )}
          {formData.pm_source === 'custom' && (
            <input
              type="text"
              name="project_manager_custom"
              value={formData.project_manager_custom}
              onChange={handleChange}
              placeholder="Project manager name (e.g. Erik Martin, or a site super)"
              className={inputClasses}
              maxLength={160}
            />
          )}
        </div>

        {/* Budget and Status */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="budget" className={labelClasses}>
              Budget
            </label>
            <input
              type="number"
              id="budget"
              name="budget"
              value={formData.budget || ''}
              onChange={handleChange}
              className={inputClasses}
              placeholder="25000"
              min="0"
              step="0.01"
            />
            {errors.budget && <p className={errorClasses}>{errors.budget}</p>}
          </div>

          <div>
            <label htmlFor="status" className={labelClasses}>
              Status
            </label>
            <select
              id="status"
              name="status"
              value={formData.status || 'scheduled'}
              onChange={handleChange}
              className={inputClasses}
            >
              <option value="scheduled">Scheduled</option>
              <option value="in_progress">In Progress</option>
              <option value="on_hold">On Hold</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className={labelClasses}>
            Description / Notes
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description || ''}
            onChange={handleChange}
            className="form-textarea flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-lg text-gray-900 focus:outline-0 focus:ring-2 focus:ring-primary border-gray-200 bg-gray-50 px-4 py-3 text-sm font-normal leading-normal"
            rows={4}
            placeholder="Additional details about the job..."
          />
        </div>

        {/* Buttons */}
        <div className="flex gap-4 justify-end pt-4">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="flex min-w-[84px] cursor-pointer items-center justify-center overflow-hidden rounded-lg h-10 px-4 bg-gray-50 text-gray-900 border border-gray-200 text-sm font-bold leading-normal tracking-[0.015em] hover:bg-gray-100"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            className="flex min-w-[84px] cursor-pointer items-center justify-center overflow-hidden rounded-lg h-10 px-4 bg-primary text-white text-sm font-bold leading-normal tracking-[0.015em] hover:bg-primary/90"
          >
            {initialData ? 'Update Job' : 'Create Job'}
          </button>
        </div>
      </div>
    </form>
  );
};

